import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    withStyles,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import SearchIcon from '@material-ui/icons/Search';
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike';
import RefreshIcon from '@material-ui/icons/Refresh';
import useEvReportStyles from './useEvReportStyles';
import { API_URL } from '../../../config/env/env';
import { getUserToken } from '../../../utils/helper';

// ─── Columns ─────────────────────────────────────────────────────────────────
const COLUMNS = [
    { id: 'vid',               label: 'VIN' },
    { id: 'description',       label: 'Vehicle Type' },
    { id: 'registrationNumber',label: 'Reg. Number' },
    { id: 'totalDrives',       label: 'Total Drive / Vehicle Swaps' },
    { id: 'swaps',             label: 'Battery Swaps' },
    { id: 'totalSocConsumed',  label: 'Total SOC Consumed' },
    { id: 'maxDistance',       label: 'Distance',           unit: 'km' },
    { id: 'totalKwhrConsumed', label: 'Total kWhr Consumed', unit: 'kWh' },
    { id: 'dod',               label: 'Avg. DOD',           unit: '%' },
    { id: 'kmPerSwap',         label: 'KM / Vehicle Swap',  unit: 'km' },
];

// ─── Styled cells ────────────────────────────────────────────────────────────
const StyledHeadCell = withStyles(() => ({
    head: {
        fontWeight: 700,
        fontSize: 12,
        color: '#525050',
        backgroundColor: '#F6F7F9',
        padding: '10px 14px',
        borderBottom: '1px solid #EBEDF8',
        whiteSpace: 'nowrap',
    },
}))(TableCell);

const StyledCell = withStyles(() => ({
    body: {
        fontSize: 13,
        fontWeight: 500,
        color: '#14112D',
        padding: '10px 14px',
        borderBottom: '1px solid #EBEDF8',
        whiteSpace: 'nowrap',
    },
}))(TableCell);

// ─── Component ───────────────────────────────────────────────────────────────
const EvReportPage: React.FC = (props: any) => {
    const classes = useEvReportStyles();

    const today     = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const [loading, setLoading]           = useState(false);
    const [allData, setAllData]           = useState<any[]>([]);
    const [startDate, setStartDate]       = useState<Date | null>(yesterday);
    const [endDate, setEndDate]           = useState<Date | null>(today);
    const [searchText, setSearchText]     = useState('');
    const [page, setPage]                 = useState(0);
    const [rowsPerPage, setRowsPerPage]   = useState(50);
    const [error, setError]               = useState<string | null>(null);
    const [sortCol, setSortCol]           = useState<string>('');
    const [sortDir, setSortDir]           = useState<'asc' | 'desc'>('asc');

    // ── Sort handler ──────────────────────────────────────────────────────
    const handleSort = (colId: string) => {
        if (sortCol === colId) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(colId);
            setSortDir('asc');
        }
        setPage(0);
    };

    // ── Filtered + sorted rows ────────────────────────────────────────────
    const filtered = allData.filter((row) => {
        if (!searchText) return true;
        const q = searchText.toLowerCase();
        return (
            String(row.vid ?? '').toLowerCase().includes(q) ||
            String(row.registrationNumber ?? '').toLowerCase().includes(q) ||
            String(row.description ?? '').toLowerCase().includes(q)
        );
    });

    const sorted = sortCol
        ? [...filtered].sort((a, b) => {
              const av = a[sortCol] ?? '';
              const bv = b[sortCol] ?? '';
              const cmp =
                  typeof av === 'number' && typeof bv === 'number'
                      ? av - bv
                      : String(av).localeCompare(String(bv));
              return sortDir === 'asc' ? cmp : -cmp;
          })
        : filtered;

    const pageRows = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // ── Load from backend ─────────────────────────────────────────────────
    const load = useCallback(async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setError(null);

        const startTime = moment(startDate).startOf('day').valueOf();
        const endTime   = moment(endDate).endOf('day').valueOf();

        try {
            const res = await axios.get(`${API_URL}/org/ev/indofast/sun-mobility/vehicle-summary`, {
                params: { startTime, endTime },
                headers: { authtoken: getUserToken() },
            });

            const raw: any[] = res.data?.response?.vehicles ?? [];

            // Enrich with computed kmPerSwap
            const enriched = raw.map((r: any) => ({
                ...r,
                kmPerSwap: r.swaps > 0 ? +(r.maxDistance / r.swaps).toFixed(2) : null,
            }));

            setAllData(enriched);
            setPage(0);
        } catch (err: any) {
            setError(err?.response?.data?.status?.message ?? err?.message ?? 'Failed to fetch report');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    // Auto-load when dates change
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // ── Cell renderer ─────────────────────────────────────────────────────
    const renderCell = (col: typeof COLUMNS[0], row: any) => {
        const val = row[col.id];

        if (val == null) return <Typography style={{ color: 'rgba(20,17,45,0.35)', fontSize: 13 }}>—</Typography>;

        const display = typeof val === 'number'
            ? Number.isInteger(val) ? val.toLocaleString() : val.toFixed(2)
            : String(val);

        return (
            <Box display="flex" alignItems="baseline" style={{ gap: 3 }}>
                <Typography className={classes.metricValue}>{display}</Typography>
                {col.unit && (
                    <Typography component="span" className={classes.metricUnit}>{col.unit}</Typography>
                )}
            </Box>
        );
    };

    return (
        <Paper className={classes.container} elevation={0} style={{ padding: 0 }}>

            {/* ── Header ─────────────────────────────────────────────── */}
            <Box className={classes.headerBox} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center">
                    <Button
                        style={{ minWidth: 0, color: '#000', marginRight: 12 }}
                        onClick={() => props?.history?.goBack()}
                    >
                        <ArrowBackIosIcon style={{ fontSize: 16 }} />
                    </Button>
                    <Box>
                        <Typography className={classes.pageTitle}>Vehicle Report</Typography>
                        <Typography className={classes.pageSubTitle}>Sun Mobility</Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh">
                    <span>
                        <Button
                            className={classes.applyCTA}
                            onClick={load}
                            variant="contained"
                            disableElevation
                            size="small"
                            disabled={loading}
                            startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
                        >
                            Refresh
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {/* ── Filters ────────────────────────────────────────────── */}
            <Box className={classes.filtersRow}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
                        <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            inputVariant="outlined"
                            format="dd MMM yyyy"
                            label="Start Date"
                            value={startDate}
                            onChange={(d) => { setStartDate(d); setPage(0); }}
                            maxDate={endDate ?? new Date()}
                            KeyboardButtonProps={{ 'aria-label': 'start date' }}
                            className={classes.dateInput}
                            style={{ width: 180 }}
                            autoOk
                        />
                        <Typography className={classes.dateSeparator}>→</Typography>
                        <KeyboardDatePicker
                            disableToolbar
                            variant="inline"
                            inputVariant="outlined"
                            format="dd MMM yyyy"
                            label="End Date"
                            value={endDate}
                            onChange={(d) => { setEndDate(d); setPage(0); }}
                            minDate={startDate ?? undefined}
                            maxDate={new Date()}
                            KeyboardButtonProps={{ 'aria-label': 'end date' }}
                            className={classes.dateInput}
                            style={{ width: 180 }}
                            autoOk
                        />
                        <TextField
                            variant="outlined"
                            placeholder="Search VIN / Reg. No."
                            value={searchText}
                            onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                            style={{ width: 240 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: '#9E9E9E' }} />
                                    </InputAdornment>
                                ),
                                style: { borderRadius: 8, fontSize: 13 },
                            }}
                            inputProps={{ style: { padding: '9px 4px' } }}
                        />
                        <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: 'auto' }}>
                            {!loading && allData.length > 0 && (
                                <Typography style={{ fontSize: 13, color: 'rgba(20,17,45,0.5)' }}>
                                    {filtered.length} vehicle{filtered.length !== 1 ? 's' : ''}
                                </Typography>
                            )}
                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <Typography style={{ fontSize: 13, color: 'rgba(20,17,45,0.6)', whiteSpace: 'nowrap' }}>
                                    Show
                                </Typography>
                                <Select
                                    variant="outlined"
                                    value={rowsPerPage}
                                    onChange={(e: any) => {
                                        setRowsPerPage(Number(e.target.value));
                                        setPage(0);
                                    }}
                                    style={{ height: 36, fontSize: 13, minWidth: 80 }}
                                >
                                    {[50, 100, 250, 500].map((n) => (
                                        <MenuItem key={n} value={n} style={{ fontSize: 13 }}>{n}</MenuItem>
                                    ))}
                                </Select>
                                <Typography style={{ fontSize: 13, color: 'rgba(20,17,45,0.6)', whiteSpace: 'nowrap' }}>
                                    entries
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </MuiPickersUtilsProvider>
            </Box>

            {/* ── Error ──────────────────────────────────────────────── */}
            {error && (
                <Box px={3} py={1} style={{ backgroundColor: '#FFF3F3', borderBottom: '1px solid #FFCDD2' }}>
                    <Typography style={{ fontSize: 13, color: '#C62828' }}>{error}</Typography>
                </Box>
            )}

            {/* ── Table ──────────────────────────────────────────────── */}
            <Box className={classes.tableSection} pt={2}>
                {loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} style={{ gap: 12 }}>
                        <CircularProgress size={36} />
                        <Typography style={{ fontSize: 13, color: 'rgba(20,17,45,0.5)' }}>
                            Fetching report…
                        </Typography>
                    </Box>
                ) : pageRows.length === 0 ? (
                    <Box className={classes.noDataBox}>
                        <DirectionsBikeIcon style={{ fontSize: 56, color: '#D0D5E8' }} />
                        <Typography className={classes.noDataText}>
                            No data for the selected date range.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer className={classes.tableContainer}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map((col) => (
                                            <StyledHeadCell key={col.id} sortDirection={sortCol === col.id ? sortDir : false}>
                                                <TableSortLabel
                                                    active={sortCol === col.id}
                                                    direction={sortCol === col.id ? sortDir : 'asc'}
                                                    onClick={() => handleSort(col.id)}
                                                    style={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </StyledHeadCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageRows.map((row: any, i: number) => (
                                        <TableRow
                                            key={row.vid + i}
                                            hover
                                            style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#FAFBFF' }}
                                        >
                                            {COLUMNS.map((col) => (
                                                <StyledCell key={col.id}>
                                                    {renderCell(col, row)}
                                                </StyledCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[]}
                            component="div"
                            count={sorted.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={(_: any, p: number) => setPage(p)}
                            onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default EvReportPage;
